import DexieDB from './DexieDB';
import DexieDBProvider from './DexieDBProvider';
import {
    EventsService,
    DataSetService,
    AppSettingsService,
    OnlineService,
    ConfigurationService,
    AxDataService,
    QuickGuideService,
    QuickEditService,
    FilterNewService,
    MyMillDataService,
} from './index';
import { DataSetStore } from '@/store/store.dataSet';
import { SettingsReportStore } from '@/store/store.settingsReport';
import { QuickGuideStore } from '@/store/store.quickGuide';
import { EventStore } from '@/store/store.event';
import { ConfigurationField } from '@/models/configuration/Field';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { Configuration } from '@/models/configuration/Configuration';
import { LoggerTypeEnum } from '@/enums/LoggerTypeEnums';
import { LoggerService } from '@/utils';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { AuthStore } from '@/store/store.auth';

class SynchronizationProivider {
    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;

    private lastSync?: Date;
    private syncTimer?: any;
    private syncTimeout: number = parseInt(process.env.VUE_APP_SYNC_TIMEOUT || '60000', 10);

    // tslint:disable-next-line: no-empty
    constructor() {
        // if online status changes to online from offline, sync
        OnlineService.OnOnlineStausChanged.on((inStatus) => {
            if (inStatus && inStatus.old !== inStatus.new && inStatus.new === OnlineStatusEnums.ONLINE) {
                this.SyncNow();
            }
        });
    }

    /**
     * Method to start the automatic synchronization with the server
     */
    public StartSyncService() {
        this.clearSyncTimer();
        const self = this;
        this.syncTimer = window.setInterval(async () => {
            await self.syncData();
            await self.syncFilter();
        }, this.syncTimeout);
    }

    /**
     * Methods to stop the automatic synchronization with the server
     */
    public StopSyncService() {
        this.clearSyncTimer();
    }

    public async SyncNow() {
        let restart: boolean = false;
        if (this.IsRunnung() === true) {
            // stop timed sync
            this.clearSyncTimer();
            restart = true;
        }
        // sync now
        this.syncData().finally(() => {
            if (restart === true) {
                // restart sync service
                this.StartSyncService();
            }
        });
    }

    public async SyncFiltersNow() {
        let restart: boolean = false;
        if (this.IsRunnung() === true) {
            // stop timed sync
            this.clearSyncTimer();
            restart = true;
        }
        // sync now
        this.syncFilter().finally(() => {
            if (restart === true) {
                // restart sync service
                this.StartSyncService();
            }
        });
    }

    public async SyncNowComplete() {
        let restart: boolean = false;
        if (this.IsRunnung() === true) {
            // stop timed sync
            this.clearSyncTimer();
            restart = true;
        }
        // sync now
        await this.syncData().finally(() => {
            if (restart === true) {
                // restart sync service
                this.StartSyncService();
            }
        });
    }

    public IsRunnung(): boolean {
        return this.syncTimer !== undefined;
    }

    public async LoadConfigData(): Promise<any> {
        return new Promise(async (resolve, reject) => {
            // fetch the config file
            await ConfigurationService.FetchConfiguration()
                .then((response) => {
                    LoggerService.LogDebug(`configuration: `, response);
                    /**
                     * Line below is just for dev's to generate translation list
                     */
                    // this.generateIdentifierTableConfig(ConfigurationService.GetConfiguration);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`configuration: `, error);
                    reject(error);
                });

            // fetch the config file
            await ConfigurationService.FetchMymillImportConfiguration()
                .then((response) => {
                    LoggerService.LogDebug(`MymillImportConfiguration: `, response);
                    /**
                     * Line below is just for dev's to generate translation list
                     */
                    // this.generateIdentifierTableConfig(ConfigurationService.GetConfiguration);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`MymillImportConfiguration: `, error);
                    reject(error);
                });

            // fetch the machines config file
            await ConfigurationService.FetchMachinesConfiguration()
                .then((response) => {
                    LoggerService.LogDebug(`machinesConfiguration: `, response);
                    /**
                     * Lines below are just for dev's to generate translation lists
                     */
                    // this.generateIdentifierTableConfig(ConfigurationService.GetMachinesConfiguration);
                    // LoggerService.LogDebug(`json:\n`, this.generateImportJson());
                })
                .catch((error) => {
                    LoggerService.LogDebug(`machinesConfiguration: `, error);
                    reject(error);
                });

            // fetch the samples config file
            await ConfigurationService.FetchSamplesConfiguration()
                .then((response) => {
                    LoggerService.LogDebug(`samplesConfiguration: `, response);
                    /**
                     * Lines below are just for dev's to generate translation lists
                     */
                    // this.generateIdentifierTableConfig(ConfigurationService.GetSamplesConfiguration);
                    // LoggerService.LogDebug(`json:\n`, this.generateImportJson());
                })
                .catch((error) => {
                    LoggerService.LogDebug(`samplesConfiguration: `, error);
                    reject(error);
                });

            // fetch the units config file
            await ConfigurationService.FetchUnitsConfiguration()
                .then((response) => {
                    LoggerService.LogDebug(`unitsConfiguration: `, response);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`unitsConfiguration: `, error);
                    reject(error);
                });

            // generate the complete array of sections
            ConfigurationService.generateCompleteConfigurationSections();

            // initial get data sets from store
            await DataSetService.FetchDataSets()
                .then(async () => {
                    LoggerService.LogDebug(`FetchDataSets done`);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`Error on data set store: `, error);
                    reject(error);
                });

            await DataSetStore.FetchTempTableItems()
                .then(async (subresponse) => {
                    LoggerService.LogDebug(`FetchTempTableItems: `, subresponse);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`Error on temp table data set store: `, error);
                    reject(error);
                });

            // initial get unsynced evetns from store
            await EventStore.SetUnsyncedEvents()
                .then((response) => {
                    LoggerService.LogDebug(`SetUnsyncedEvents: `, response);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`Error on event store: `, error);
                    reject(error);
                });

            // fetch settings reports async
            await SettingsReportStore.FetchSettingsReports()
                .then((response) => {
                    LoggerService.LogDebug(`settingsReports: `, response);
                })
                .catch((error) => {
                    LoggerService.LogError(`settingsReports: `, error);
                });

            // fetch quick guide async
            await QuickGuideStore.FetchQuickGuide()
                .then((response) => {
                    LoggerService.LogDebug(`quickGuide: `, response);
                })
                .catch((error) => {
                    LoggerService.LogError(`quickGuide: `, error);
                });

            // fetch AX data stuff
            await AxDataService.UpdateAxData()
                .then((response) => {
                    LoggerService.LogDebug(`AxDataService: `, response);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`AxDataService: `, error);
                    reject(error);
                });

            // fetch/update quick view configurations
            await QuickEditService.FetchQuickEdit()
                .then((response) => {
                    LoggerService.LogDebug(`quick view configurations: `, response);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`Error fetching quick view configurations: `, error);
                    reject(error);
                });

            // fetch myMill data stuff
            await MyMillDataService.UpdateMyMillUnits()
                .then(() => {
                    LoggerService.LogDebug(`MyMillDataService updated unit uitems.`);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`MyMillDataService error: `, error);
                    reject(error);
                });

            // fetch filters async
            await FilterNewService.SyncFiltersWithServer(false)
                .then((response) => {
                    LoggerService.debug(`FilterNewService: `, response);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`FilterNewService: `, error);
                });

            resolve(true);
        });
    }

    private async syncData() {
        await OnlineService.FetchIsOnline().catch((error) => {
            LoggerService.LogError(`Error on fetching online status: `, error);
        });
        // sync unsynced events to server if online
        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
            await EventsService.checkForAutosync()
                .then(async () => {
                    // fetch data sets/events from server
                    await DataSetService.FetchDataSets()
                        .then(async (updatesDone) => {
                            AppSettingsService.UpdateLastSync('OK');
                            if (updatesDone.length > 0) {
                                DataSetService.TriggerUpdateEvent(updatesDone);
                            }
                        })
                        .catch((error: any) => {
                            LoggerService.LogError(`Error on sync data sets: `, error);
                            AppSettingsService.UpdateLastSync(`Error on sync data sets: \n ${JSON.stringify(error)}`);
                        });
                })
                .catch((error: any) => {
                    LoggerService.LogError(`Error on sync events: `, error);
                    AppSettingsService.UpdateLastSync(`Error on sync events: \n ${JSON.stringify(error)}`);
                });
        }
    }

    private async syncFilter() {
        await OnlineService.FetchIsOnline().catch((error) => {
            LoggerService.LogError(`Error on fetching online status: `, error);
        });
        // sync unsynced events to server if online
        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
            // fetch filters async
            await FilterNewService.SyncFiltersWithServer()
                .then((response) => {
                    LoggerService.debug(`FilterNewService: `, response);
                })
                .catch((error) => {
                    LoggerService.LogDebug(`FilterNewService: `, error);
                });
        }
    }

    private clearSyncTimer() {
        if (this.syncTimer !== undefined) {
            window.clearInterval(this.syncTimer);
            this.syncTimer = undefined;
        }
    }

    // methods below are only for development

    /**
     * Method for debug only. This is to generate the translation strings for each identifier
     * @param conf  A config object
     */
    private generateIdentifierTableConfig(conf: Configuration) {
        if (LoggerService.defaultLogLevel === LoggerTypeEnum.DEBUG) {
            let table: string = '';
            conf.sections.forEach((section) => {
                table += `"__comment_${section.name.replace(/\s/g, '_')}": "${section.name}",\n`;
                table += `"${section.identifier}": "${section.name}",\n`;
                section.fields.forEach((field) => {
                    // table += `\t"${field.identifier}": "${field.name}",\n`;
                    table += this.generateFieldsIdentifierTableConfig(field, `\t`);
                });
            });

            LoggerService.LogDebug(table);
        }
    }

    /**
     * Method for debug only. This is to generate the translation strings for each identifier
     * @param field     A field object
     * @param prefix    A prefix
     */
    private generateFieldsIdentifierTableConfig(field: ConfigurationField, prefix?: string): string {
        let fieldsString: string = '';
        let counter: number = 0;
        if (field.type === FieldTypeEnum.SUBFIELD || field.type === FieldTypeEnum.ACTION_SUBFIELD) {
            if (field.fields) {
                fieldsString += `${prefix}"__comment_${field.name.replace(/\s/g, '_')}": "${field.name}",\n`;
                fieldsString += `${prefix}"${field.identifier}": "${field.name}",\n`;
                for (const subField of field.fields) {
                    fieldsString += this.generateFieldsIdentifierTableConfig(subField, `${prefix}\t`);
                    counter++;
                }
            }
        } else {
            fieldsString += `${prefix}"${field.identifier}": "${field.name}",\n`;
        }

        return fieldsString;
    }

    private generateImportJson(): string {
        let counter = 0;
        let json = '{"sections":[';
        const config = ConfigurationService.GetConfiguration;
        const machines = ConfigurationService.GetMachinesConfiguration;
        // for (const section of config.sections) {
        //     json += `{"name": "${section.name}", "identifier": "${section.identifier}", "fields": [`;
        //     for (const field of section.fields) {
        //         json += this.generateJsonFields(field);
        //     }
        //     json += ']},';
        //     counter++;
        // }

        for (const machine of machines.sections) {
            json += `{"name": "${machine.name}", "identifier": "${machine.identifier}", "fields": [`;
            for (const field of machine.fields) {
                json += this.generateJsonFields(field);
            }
            json += ']},';
            counter++;
        }
        json += ']}';

        return json;
    }

    private generateJsonFields(field: ConfigurationField): string {
        let json = `{"name": "${field.name}", "identifier": "${field.identifier}"`;
        if (field.type === FieldTypeEnum.ACTION_SUBFIELD || field.type === FieldTypeEnum.SUBFIELD) {
            json += `,"fields": [`;
            if (field.fields !== undefined) {
                for (const subField of field.fields) {
                    json += this.generateJsonFields(subField);
                }
            }
            json += `]`;
        } else {
            json += `,"value": "Value of the field"`;
        }

        json += `},`;

        return json;
    }
}

export default SynchronizationProivider;
